"use client";

import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { MyAside } from "@/components/user-button-pages/MyAside";
import GuestPreviewOverlay from "@/components/user-button-pages/GuestPreviewOverlay";
import { cn } from "@/lib/utils";

/**
 * /my 통합 대시보드의 로그인 필요 탭(공부 진행상황·도전과제·필기 모음·감자 이용권·구매 목록)을
 * 감싸는 공통 셸 (JCG-2071 → JCG-2073 통합 개편).
 *
 * - 인증 가드는 여기서 담당(레이아웃이 아니라 페이지별) — /my/error-notes 는 익명 사용자도
 *   접근하므로 이 셸을 쓰지 않는다.
 * - 데스크탑 3컬럼 custom 셸 안(`inShell`)에서는 좌측 탭 네비를 `my/MyLayoutClient` 가, 상단바 제목을
 *   ContentHeader 가 이미 제공하므로 여기선 본문 폭만 제약한다(자체 제목/컨테이너 패딩 없음).
 *   `rightContext`(탭별 맥락 카드)는 우측 사이드바 슬롯(customRightSlot)에 portal.
 * - 모바일/클래식(셸 밖)에서는 기존처럼 컨테이너로 감싸고, 맥락 카드는 본문 하단에 렌더한다.
 *
 * 각 탭 페이지 컴포넌트는 자체 `<h2>` 제목을 가지므로 여기서 제목은 넣지 않는다.
 */
export default function ProfileRouteShell({
  title,
  children,
  rightContext,
  mobileTopContext,
  mobileRightContext,
  wide = false,
  guestPreview,
  guestDescription,
}: {
  /** 비로그인 안내에 쓰는 페이지 이름 */
  title: string;
  children: ReactNode;
  /** 우측 사이드바(데스크탑) / 본문 하단(모바일)에 표시할 탭별 맥락 카드 */
  rightContext?: ReactNode;
  /** 모바일/태블릿 상단에 sticky로 고정할 핵심 컨트롤 */
  mobileTopContext?: ReactNode;
  /** 모바일/태블릿 본문 하단에 표시할 맥락 카드. 미지정 시 rightContext를 그대로 쓴다. */
  mobileRightContext?: ReactNode;
  /**
   * 본문 폭을 넓힌다(JCG-2161). 읽기형 단일 컬럼 탭은 기본(좁은 폭)이지만, 구매 목록처럼 여러 카드를
   * 가로로 배치하는 탭은 폭이 좁으면 텍스트 줄바꿈이 과해진다. /my 셸 본문은 폭 캡이 없어(isMyDashboard)
   * 이 값으로 컬럼을 더 채운다.
   */
  wide?: boolean;
  /**
   * 비로그인(게스트) 상태에서 흐리게 깔 목업 JSX(학습 탭 게스트 프리뷰). 주어지면 기존 단순
   * "로그인이 필요합니다" 메시지 대신 `GuestPreviewOverlay`(흐린 목업 + 로그인 CTA)를 본문에 렌더한다.
   * 없으면 기존 단순 메시지 그대로(pass·purchases 탭은 이 prop 을 안 넘기므로 영향 없음).
   */
  guestPreview?: ReactNode;
  /** 게스트 프리뷰 오버레이 카드의 설명 문구(미지정 시 일반 안내). `guestPreview` 와 함께 쓴다. */
  guestDescription?: string;
}) {
  const { isSignedIn, isLoaded } = useUser();
  const { inShell, customRightSlot } = useArticleShellSlots();

  if (!isLoaded) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <>
        {guestPreview ? (
          // 학습 탭(공부 진행상황·도전과제·필기 모음 등): 흐린 목업 + 가운데 로그인 CTA(JCG).
          // 프리뷰가 충분히 넓게 보이도록 셸 안에서는 본문 폭 그대로, 셸 밖에선 컨테이너만 감싼다.
          <div className={inShell ? "" : "container mx-auto px-4 pb-6 pt-3 sm:py-8"}>
            <GuestPreviewOverlay
              title={title}
              description={guestDescription ?? "로그인하면 이 페이지를 볼 수 있어요."}
            >
              {guestPreview}
            </GuestPreviewOverlay>
          </div>
        ) : (
          <div className={inShell ? "py-12" : "container mx-auto max-w-4xl px-4 pb-6 pt-3 sm:py-8"}>
            <div className="py-12 text-center">
              <h1 className="mb-4 text-2xl font-bold text-foreground">{title}</h1>
              <p className="mb-4 text-muted-foreground">로그인이 필요합니다.</p>
              <Button asChild>
                <Link href="/sign-in">로그인하기</Link>
              </Button>
            </div>
          </div>
        )}
        {/* 셸 우측 슬롯이 빈 채로 테두리만 남지 않도록 로그인 안내 카드를 채운다(JCG-2073). */}
        {inShell &&
          customRightSlot &&
          createPortal(
            <MyAside icon={LogIn} title="로그인 안내">
              <p>로그인하면 학습 기록과 이용권을 한 곳에서 확인할 수 있어요.</p>
              <Button asChild size="sm" className="mt-1 w-full">
                <Link href="/sign-in">로그인하기</Link>
              </Button>
            </MyAside>,
            customRightSlot,
          )}
      </>
    );
  }

  // 데스크탑 셸: 본문 폭만 제약(좌측 네비·상단바 제목은 셸이 제공) + 맥락 카드는 우측 슬롯에 portal.
  // 탭 본문 컴포넌트는 <h2>로 시작하므로, 문서 아웃라인을 위해 시각적으로 숨긴 <h1>(=탭 제목)을 둔다.
  if (inShell) {
    return (
      <>
        <div className={cn("mx-auto", wide ? "max-w-5xl" : "max-w-3xl")}>
          <h1 className="sr-only">{title}</h1>
          {children}
        </div>
        {rightContext && customRightSlot && createPortal(rightContext, customRightSlot)}
      </>
    );
  }

  // 모바일/클래식: 컨테이너로 감싸고 맥락 카드는 본문 하단.
  const bottomContext = mobileRightContext ?? rightContext;

  return (
    <div
      className={cn(
        "container mx-auto px-4 pb-6 pt-3 sm:py-8",
        wide ? "max-w-5xl" : "max-w-4xl",
      )}
    >
      <h1 className="sr-only">{title}</h1>
      {mobileTopContext && <div className="lg:hidden">{mobileTopContext}</div>}
      {children}
      {bottomContext && <div className="mt-8">{bottomContext}</div>}
    </div>
  );
}
